import React, { useEffect, useMemo, useState } from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import PageShell, { SectionCard } from "components/patch/PageShell";
import SimpleTable from "components/patch/SimpleTable";
import { listBulkTemplates, templateUrl, uploadBulkModification } from "services/bulkModifications";

export default function BulkModificationCenter() {
  const currentMonth = useMemo(() => new Date().toISOString().slice(0, 7), []);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState("");
  const [effectiveMonth, setEffectiveMonth] = useState(currentMonth);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [createdBatch, setCreatedBatch] = useState(null);

  const load = async () => {
    try {
      const rows = await listBulkTemplates();
      setTemplates(rows);
      if (!selected && rows.length) setSelected(rows[0].key);
    } catch (e) {
      setMessage(e?.response?.data?.message || e.message || "Unable to load templates");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const selectedTemplate = templates.find((t) => t.key === selected);

  const submit = async () => {
    if (!selected || !file || !effectiveMonth) {
      setMessage("Select a category, effective month, and upload file.");
      return;
    }

    setLoading(true);
    setMessage("");
    setCreatedBatch(null);

    try {
      const res = await uploadBulkModification(selected, {
        file,
        effective_month: effectiveMonth,
        title: title || selectedTemplate?.name,
      });
      setCreatedBatch(res.data);
      setMessage(res.message || "Upload completed. Review the variation batch before approval.");
      setFile(null);
    } catch (e) {
      setMessage(e?.response?.data?.message || e.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const rows = templates.map((t) => ({
    category: t.name,
    key: t.key,
    type: t.variation_type,
    action: (
      <MDButton
        size="small"
        color="info"
        component="a"
        href={templateUrl(t.key)}
        target="_blank"
        rel="noreferrer"
      >
        Download Template
      </MDButton>
    ),
  }));

  return (
    <PageShell
      title="Bulk Modification Center"
      subtitle="Download sample templates and upload bulk modifications. Uploads create variation batches for review, approval, and controlled application."
    >
      <SectionCard title="Upload Bulk Modification">
        <MDBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
          <MDBox>
            <MDTypography variant="button" fontWeight="medium">
              Modification Category
            </MDTypography>
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              style={{
                width: "100%",
                height: 42,
                borderRadius: 8,
                padding: "0 12px",
                border: "1px solid #d2d6da",
              }}
            >
              {templates.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.name}
                </option>
              ))}
            </select>
          </MDBox>

          <MDInput
            label="Effective Month"
            type="month"
            value={effectiveMonth}
            onChange={(e) => setEffectiveMonth(e.target.value)}
            fullWidth
          />

          <MDInput
            label="Batch Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
          />

          <MDBox>
            <MDTypography variant="button" fontWeight="medium">
              Excel/CSV File
            </MDTypography>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              style={{ width: "100%", marginTop: 8 }}
            />
          </MDBox>
        </MDBox>

        <MDBox mt={2} display="flex" gap={1} flexWrap="wrap">
          {selected && (
            <MDButton
              color="secondary"
              component="a"
              href={templateUrl(selected)}
              target="_blank"
              rel="noreferrer"
            >
              Download Selected Template
            </MDButton>
          )}
          <MDButton color="info" onClick={submit} disabled={loading}>
            {loading ? "Uploading..." : "Upload and Create Batch"}
          </MDButton>
        </MDBox>

        {message && (
          <MDTypography
            mt={2}
            variant="body2"
            color={message.toLowerCase().includes("fail") ? "error" : "text"}
          >
            {message}
          </MDTypography>
        )}

        {createdBatch && (
          <MDBox mt={2} p={2} borderRadius="lg" bgColor="grey-100">
            <MDTypography variant="body2">
              <strong>Batch:</strong> {createdBatch.batch_ref}
            </MDTypography>
            <MDTypography variant="body2">
              <strong>Status:</strong> {createdBatch.status}
            </MDTypography>
            <MDTypography variant="body2">
              <strong>Valid:</strong> {createdBatch.valid_items} / <strong>Invalid:</strong>{" "}
              {createdBatch.invalid_items}
            </MDTypography>
            <MDTypography variant="caption">
              Open Variation Batches to review items and submit for approval.
            </MDTypography>
          </MDBox>
        )}
      </SectionCard>

      <SectionCard title="Available Templates">
        <SimpleTable
          columns={[
            { Header: "Category", accessor: "category" },
            { Header: "Key", accessor: "key" },
            { Header: "Variation Type", accessor: "type" },
            { Header: "Action", accessor: "action" },
          ]}
          rows={rows}
        />
      </SectionCard>
    </PageShell>
  );
}
